import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Users, Palette, Wand2, LineChart, ShieldCheck, Sparkles, Check, X, Quote, ChevronDown, Zap, Download, Globe2, MousePointer2, Type } from "lucide-react";
import { api } from "../../lib/api";
import { THEME } from "../design/tokens";
import { MarketingNav, MarketingFooter, SectionShell, EditorialHeading, Eyebrow, Lede, Button, Pill, Card } from "../design/Marketing";

/** Skeleton page shell for every marketing route. */
export function MarketingLayout({ children }) {
  return (
    <div style={THEME}>
      <MarketingNav />
      <main>{children}</main>
      <MarketingFooter />
    </div>
  );
}

// ---------------------------------------------------------------- HERO
function Hero() {
  return (
    <SectionShell size="lg" dataTestId="hero" className="pt-10 sm:pt-16 lg:pt-20">
      <div className="grid grid-cols-1 gap-14 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:gap-16">
        <div>
          <Pill className="mb-6">Built by a creator, for creators</Pill>
          <EditorialHeading as="h1" size="xxl">
            Your link in bio should<br />do more than <em style={{ fontStyle: "italic", fontVariationSettings: "'opsz' 144" }}>hold links.</em>
          </EditorialHeading>
          <Lede className="mt-6 max-w-xl">
            Build a page that looks like you, understand what your audience clicks, collect leads, and update everything in minutes.
          </Lede>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button to="/signup" size="lg" variant="primary" testid="hero-cta-primary">
              Build my page free <ArrowRight size={16} />
            </Button>
            <Button href="#how-it-works" size="lg" variant="secondary" testid="hero-cta-secondary">See how it works</Button>
          </div>
          <p className="mt-5 text-[13px]" style={{ color: "var(--lm-ink-3)" }}>
            30 days free. No payment method required.
          </p>
        </div>
        <HeroPreview />
      </div>
    </SectionShell>
  );
}

/** Editorial "product screenshot" preview — real UI, not a decorative mock. */
function HeroPreview() {
  return (
    <div className="relative">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[32px]" style={{ background: "var(--lm-cream-2)" }} />
      <div className="grid grid-cols-[1.6fr_1fr] items-start gap-4">
        {/* Editor sketch */}
        <Card className="overflow-hidden">
          <div className="border-b px-4 py-3" style={{ borderColor: "var(--lm-hair)" }}>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#E5D8C1" }} />
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#E5D8C1" }} />
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#E5D8C1" }} />
              <span className="ml-3 text-[11px] font-medium uppercase tracking-[0.18em]" style={{ color: "var(--lm-ink-4)" }}>Editor</span>
            </div>
          </div>
          <div className="space-y-3 p-4">
            <PreviewRow icon={<Type size={14} />} label="Profile" value="Tester Bio" />
            <PreviewRow icon={<Palette size={14} />} label="Theme" value="Warm sand" tint />
            <PreviewRow icon={<Sparkles size={14} />} label="Block · Product" value="Ridge Wallet · 10% off" />
            <PreviewRow icon={<Sparkles size={14} />} label="Block · Link" value="Latest YouTube video" />
            <PreviewRow icon={<Sparkles size={14} />} label="Block · Contact" value="Booking form" />
          </div>
        </Card>
        {/* Mobile preview */}
        <div className="relative mt-6 rounded-[26px] p-1 shadow-[0_28px_60px_-30px_rgba(26,22,19,0.35)]" style={{ background: "var(--lm-ink)" }}>
          <div className="rounded-[22px] p-4" style={{ background: "linear-gradient(160deg,#241e19,#40342a)", minHeight: 320 }}>
            <div className="mx-auto mt-2 flex flex-col items-center">
              <div className="h-14 w-14 rounded-full ring-2 ring-white/20" style={{ background: "var(--lm-terra)" }} />
              <p className="mt-2 text-[13px] font-bold text-white">Tester Bio</p>
              <p className="mt-1 text-[10px] text-white/60">Weekly recs · India</p>
              <div className="mt-4 w-full space-y-2">
                {["Latest video", "Book a call", "Shop my picks"].map((l) => (
                  <div key={l} className="rounded-full py-2 text-center text-[11px] font-semibold text-white" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)" }}>{l}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewRow({ icon, label, value, tint }) {
  return (
    <div className="flex items-center gap-3 rounded-[10px] border p-3" style={{ borderColor: "var(--lm-hair)", background: tint ? "var(--lm-terra-light)" : "transparent" }}>
      <span className="flex h-7 w-7 items-center justify-center rounded-md" style={{ background: tint ? "var(--lm-terra)" : "var(--lm-cream-2)", color: tint ? "#fff" : "var(--lm-ink-2)" }}>{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--lm-ink-4)" }}>{label}</p>
        <p className="truncate text-[13px] font-semibold" style={{ color: "var(--lm-ink)" }}>{value}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- FOUNDER
function FounderSection({ branding }) {
  const b = branding || {};
  return (
    <SectionShell dataTestId="founder-section">
      <div className="grid grid-cols-1 gap-10 border-t pt-14 md:grid-cols-[280px_1fr] md:gap-16 md:pt-20" style={{ borderColor: "var(--lm-hair)" }}>
        <div>
          <div className="aspect-[4/5] w-full overflow-hidden rounded-[var(--lm-r-lg)] border" style={{ borderColor: "var(--lm-hair)", background: "var(--lm-cream-2)" }}>
            {b.founder_avatar_url ? (
              <img src={b.founder_avatar_url} alt={b.founder_name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center" style={{ fontFamily: "var(--lm-display)", color: "var(--lm-ink-3)", fontSize: 96 }}>
                {(b.founder_name || "?").charAt(0)}
              </div>
            )}
          </div>
          <p className="mt-4 text-[13px] font-semibold" style={{ color: "var(--lm-ink-2)" }}>{b.founder_name}</p>
          <p className="text-[12px]" style={{ color: "var(--lm-ink-4)" }}>{b.founder_handle}</p>
        </div>
        <div>
          <Eyebrow>The founder</Eyebrow>
          <EditorialHeading size="xl">I needed a better link in bio. So I built one.</EditorialHeading>
          <Lede className="mt-5">"{b.founder_bio}"</Lede>
          {b.founder_social_url && (
            <a href={b.founder_social_url} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-1 text-[14px] font-semibold underline-offset-4 hover:underline" style={{ color: "var(--lm-terra)" }}>
              Follow @{b.founder_handle} <ArrowRight size={14} />
            </a>
          )}
        </div>
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------- MOBILE APPS
function MobileAppSection() {
  // Three-phone parallax carousel — each phone floats gently with a staggered
  // delay so the section reads as "the same page, three places at once".
  const shots = [
    { src: "/mobile/screen-home.png", label: "Dashboard", delay: "0s" },
    { src: "/mobile/screen-edit.png", label: "Editor", delay: "-1.4s" },
    { src: "/mobile/screen-analytics.png", label: "Analytics", delay: "-2.8s" },
  ];
  return (
    <SectionShell dataTestId="mobile-apps-section">
      <style>{`
        @keyframes lm-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-14px); } }
        .lm-phone-float { animation: lm-float 4.2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .lm-phone-float { animation: none; } }
      `}</style>
      <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-20">
        <div>
          <Eyebrow>Now on iOS &amp; Android</Eyebrow>
          <EditorialHeading size="xl">Edit, preview and ship from your phone.</EditorialHeading>
          <Lede className="mt-5">
            The Linkit mobile app is a native creator dashboard — not a wrapper around this website.
            Your account, your page, your leads and your analytics stay perfectly in sync across every surface.
          </Lede>
          <ul className="mt-6 space-y-2 text-[14px]" style={{ color: "var(--lm-ink-2)" }}>
            <li>· Full block editor with drag-to-reorder</li>
            <li>· Draft &amp; publish safely from anywhere</li>
            <li>· Real-time analytics, top links, and lead alerts</li>
            <li>· Push notifications for new leads and traffic milestones</li>
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="https://apps.apple.com/app/linkit/id0000000000" target="_blank" rel="noreferrer"
              data-testid="marketing-download-ios"
              className="inline-flex items-center gap-2 rounded-full bg-[color:var(--lm-ink)] px-5 py-3 text-[13px] font-bold text-white hover:opacity-90"
            >
              <svg width="16" height="16" viewBox="0 0 384 512" fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM254 84.7q34.5-40.9 31.3-93.1-45.2 3.7-83.4 41.9-37.4 32.2-31.4 87.3 50.2 3.9 83.5-36.1z"/></svg>
              Download for iPhone
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=app.linkit.creator" target="_blank" rel="noreferrer"
              data-testid="marketing-download-android"
              className="inline-flex items-center gap-2 rounded-full bg-[color:var(--lm-ink)] px-5 py-3 text-[13px] font-bold text-white hover:opacity-90"
            >
              <svg width="16" height="16" viewBox="0 0 512 512" fill="currentColor"><path d="M99.617 8.057a50.191 50.191 0 00-38.815-6.713l230.932 230.933 74.846-74.846L99.617 8.057zM32.139 20.116c-6.441 8.563-10.148 19.077-10.148 30.199v411.358c0 11.123 3.708 21.636 10.148 30.199l235.877-235.877L32.139 20.116zM464.261 212.087l-67.266-37.637-81.544 81.544 81.544 81.544 67.266-37.637a49.804 49.804 0 000-87.814zM291.733 279.711L60.815 510.629c12.859 3.451 26.803 1.63 38.802-5.393l305.995-171.194-113.879-54.331z"/></svg>
              Get on Android
            </a>
          </div>
        </div>

        {/* Right column: three floating phones */}
        <div className="relative mx-auto flex h-[560px] w-full max-w-md items-end justify-center gap-3 sm:h-[600px]">
          {shots.map((s, i) => (
            <div
              key={s.src}
              data-testid={`mobile-hero-phone-${i}`}
              className="lm-phone-float relative flex-1"
              style={{
                animationDelay: s.delay,
                marginTop: i === 1 ? 0 : 40,
                zIndex: i === 1 ? 2 : 1,
                transform: i === 1 ? "translateY(-24px)" : `rotate(${i === 0 ? "-4deg" : "4deg"})`,
              }}
            >
              <img
                src={s.src}
                alt={`Linkit mobile ${s.label}`}
                loading="lazy"
                className="w-full drop-shadow-[0_20px_50px_rgba(0,0,0,0.18)]"
              />
            </div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------- HOW IT WORKS
const STEPS = [
  { n: "01", h: "Tell Linkit about your page", p: "Describe what you make, who follows you, and what you want the page to do. Or start blank — you decide." },
  { n: "02", h: "Customize the result", p: "Change words, colours, order, and blocks in minutes. You always retain full control — AI is a starting point, not a boss." },
  { n: "03", h: "Publish and learn what works", p: "Ship a live URL, share it, and see which links, products and recommendations actually get clicked." },
];
function HowItWorks() {
  return (
    <SectionShell id="how-it-works" dataTestId="how-it-works">
      <div className="max-w-3xl">
        <Eyebrow>How it works</Eyebrow>
        <EditorialHeading size="xl">Three steps. Roughly ten minutes.</EditorialHeading>
      </div>
      <div className="mt-10 grid grid-cols-1 gap-4 md:mt-14 md:grid-cols-3 md:gap-6">
        {STEPS.map((s) => (
          <Card key={s.n} className="p-6 sm:p-7">
            <p className="text-[13px] font-bold tracking-[0.18em]" style={{ color: "var(--lm-terra)" }}>{s.n}</p>
            <p className="mt-3 text-[19px] font-semibold leading-snug" style={{ color: "var(--lm-ink)" }}>{s.h}</p>
            <p className="mt-2 text-[14px] leading-relaxed" style={{ color: "var(--lm-ink-3)" }}>{s.p}</p>
          </Card>
        ))}
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------- ANALYTICS
function AnalyticsSection() {
  return (
    <SectionShell dataTestId="analytics-section" className="bg-[color:var(--lm-cream-2)]/60">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-center lg:gap-16">
        <div>
          <Eyebrow>Analytics</Eyebrow>
          <EditorialHeading size="xl">Know what people actually care about.</EditorialHeading>
          <Lede className="mt-5">
            See which recommendations, products and partnerships receive attention — without guessing. Your dashboard shows views, clicks, click-through rate, top links and daily activity.
          </Lede>
          <ul className="mt-6 space-y-3 text-[15px]" style={{ color: "var(--lm-ink-2)" }}>
            {["Page views + unique visitors", "Click-through rate per link", "Daily activity charts", "Compare periods & export CSV"].map((f) => (
              <li key={f} className="flex items-start gap-2.5"><Check size={16} className="mt-1 shrink-0" style={{ color: "var(--lm-terra)" }} /> {f}</li>
            ))}
          </ul>
          <Button to="/create/analytics" variant="secondary" className="mt-8">See analytics in detail <ArrowRight size={14} /></Button>
        </div>
        <AnalyticsMock />
      </div>
    </SectionShell>
  );
}

function AnalyticsMock() {
  const days = [12, 9, 18, 22, 16, 28, 34, 30, 26, 38, 45, 40, 52];
  const max = Math.max(...days);
  return (
    <Card className="p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--lm-ink-4)" }}>Last 14 days</p>
          <p className="mt-1 text-[28px] font-bold" style={{ color: "var(--lm-ink)", fontFamily: "var(--lm-display)" }}>1,284 clicks</p>
        </div>
        <Pill tone="terra">CTR 6.4%</Pill>
      </div>
      <div className="mt-6 flex h-32 items-end gap-1.5">
        {days.map((v, i) => (
          <div key={i} className="flex-1 rounded-t-[3px]" style={{ height: `${(v / max) * 100}%`, background: "var(--lm-ink)" }} title={`Day ${i + 1}: ${v} clicks`} />
        ))}
      </div>
      <div className="mt-6 space-y-2">
        {[
          { label: "Latest YouTube video", n: 428 },
          { label: "Ridge Wallet · 10% off", n: 264 },
          { label: "Book a call", n: 141 },
        ].map((r) => (
          <div key={r.label} className="flex items-center justify-between text-[13px]" style={{ color: "var(--lm-ink-2)" }}>
            <span className="truncate">{r.label}</span>
            <span className="font-semibold">{r.n}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- LEAD CAPTURE
function LeadsSection() {
  return (
    <SectionShell dataTestId="leads-section">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:gap-16">
        <LeadsMock />
        <div>
          <Eyebrow>Leads</Eyebrow>
          <EditorialHeading size="xl">Your audience should not belong only to a social platform.</EditorialHeading>
          <Lede className="mt-5">
            Add a lead-capture block to your page and collect names, emails, and optional phone numbers. Your leads land in your Linkit inbox — with the option to email you automatically for each one.
          </Lede>
          <ul className="mt-6 space-y-3 text-[15px]" style={{ color: "var(--lm-ink-2)" }}>
            {["Email + name + phone (optional)", "Instant email notifications", "Custom consent text & privacy link", "Export any time"].map((f) => (
              <li key={f} className="flex items-start gap-2.5"><Check size={16} className="mt-1 shrink-0" style={{ color: "var(--lm-terra)" }} /> {f}</li>
            ))}
          </ul>
        </div>
      </div>
    </SectionShell>
  );
}
function LeadsMock() {
  const leads = [
    { name: "Ananya S.", email: "ananya@…", when: "2m ago" },
    { name: "Ravi M.", email: "ravi@…", when: "1h ago" },
    { name: "Priya K.", email: "priya@…", when: "yesterday" },
  ];
  return (
    <Card className="p-5">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--lm-ink-4)" }}>Inbox</p>
      <div className="mt-3 space-y-2">
        {leads.map((l, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg border p-3" style={{ borderColor: "var(--lm-hair)" }}>
            <span className="flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-bold" style={{ background: "var(--lm-terra-light)", color: "var(--lm-terra-dark)" }}>{l.name.charAt(0)}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold" style={{ color: "var(--lm-ink)" }}>{l.name}</p>
              <p className="truncate text-[11px]" style={{ color: "var(--lm-ink-3)" }}>{l.email}</p>
            </div>
            <span className="text-[11px]" style={{ color: "var(--lm-ink-4)" }}>{l.when}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- CUSTOMIZATION
function CustomizationSection() {
  const feats = [
    { icon: Palette, t: "Themes & brand colours" },
    { icon: Type, t: "Font pairings that feel like you" },
    { icon: Sparkles, t: "Featured product cards" },
    { icon: MousePointer2, t: "Drag-and-drop reordering" },
    { icon: Users, t: "Social icon rows" },
    { icon: Zap, t: "Videos, galleries, sections" },
  ];
  return (
    <SectionShell dataTestId="customization-section" className="bg-[color:var(--lm-cream-2)]/60">
      <div className="max-w-3xl">
        <Eyebrow>Customization</Eyebrow>
        <EditorialHeading size="xl">Every block, exactly how you want it.</EditorialHeading>
        <Lede className="mt-5">
          Themes, fonts, colours, background, button styles, gallery, videos, products, socials, contact forms, collapsible sections — arrange them, hide them, rename them. It's your page.
        </Lede>
      </div>
      <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 md:mt-14 md:gap-4">
        {feats.map(({ icon: Icon, t }) => (
          <Card key={t} className="flex items-center gap-3 p-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-[10px]" style={{ background: "var(--lm-terra-light)", color: "var(--lm-terra-dark)" }}>
              <Icon size={16} />
            </span>
            <p className="text-[14px] font-medium" style={{ color: "var(--lm-ink-2)" }}>{t}</p>
          </Card>
        ))}
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------- EXAMPLES
const EXAMPLE_PAGES = [
  { name: "Content creator", desc: "Videos, newsletter, and best-of highlights.", color: "#3B2E23" },
  { name: "Travel creator", desc: "Trip archive, print shop, hotel picks.", color: "#B84A26" },
  { name: "Coach / consultant", desc: "Book a call, courses, testimonials.", color: "#1F3D2E" },
  { name: "Affiliate creator", desc: "Product carousels with tracked links.", color: "#3A1A45" },
  { name: "Small business", desc: "Menu, hours, contact, reviews.", color: "#1D3554" },
  { name: "Freelancer", desc: "Portfolio, rate card, contact form.", color: "#5C3510" },
];
function ExamplesSection() {
  return (
    <SectionShell dataTestId="examples-section">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div className="max-w-2xl">
          <Eyebrow>Made for the way you work</Eyebrow>
          <EditorialHeading size="xl">A page for every kind of creator.</EditorialHeading>
        </div>
        <Button to="/create/examples" variant="secondary">See all examples <ArrowRight size={14} /></Button>
      </div>
      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:mt-14 md:grid-cols-3">
        {EXAMPLE_PAGES.map((ex) => (
          <Card key={ex.name} className="overflow-hidden">
            <div className="aspect-[4/5] w-full" style={{ background: ex.color }}>
              <div className="flex h-full flex-col items-center justify-center gap-2 p-4">
                <div className="h-14 w-14 rounded-full" style={{ background: "rgba(255,255,255,0.14)" }} />
                <div className="h-2 w-24 rounded-full" style={{ background: "rgba(255,255,255,0.34)" }} />
                <div className="mt-3 w-full space-y-1.5">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="rounded-full py-1.5" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)" }} />
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4">
              <p className="text-[14px] font-semibold" style={{ color: "var(--lm-ink)" }}>{ex.name}</p>
              <p className="mt-1 text-[13px]" style={{ color: "var(--lm-ink-3)" }}>{ex.desc}</p>
            </div>
          </Card>
        ))}
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------- COMPARE
const COMPARE_ROWS = [
  { row: "India-first pricing", linkit: "₹499/mo · ₹4,999/yr", other: "Check current plan" },
  { row: "AI-assisted setup", linkit: "Yes", other: "Check current plan" },
  { row: "Custom design themes", linkit: "Yes", other: "Yes" },
  { row: "Link analytics", linkit: "Yes", other: "Yes (Pro)" },
  { row: "Lead collection form", linkit: "Yes", other: "Check current plan" },
  { row: "Creator-led product", linkit: "Yes", other: "—" },
  { row: "Mobile-friendly editing", linkit: "Yes", other: "Yes" },
  { row: "Monthly + annual plans", linkit: "Yes", other: "Yes" },
  { row: "Data export", linkit: "Yes", other: "Check current plan" },
];
function CompareSection() {
  return (
    <SectionShell dataTestId="compare-section">
      <div className="max-w-3xl">
        <Eyebrow>Honest comparison</Eyebrow>
        <EditorialHeading size="xl">How Linkit compares to Linktree.</EditorialHeading>
        <Lede className="mt-4">
          We only list things we've verified. Where a competitor's feature depends on their current plan, we say so.
        </Lede>
      </div>
      <Card className="mt-10 overflow-hidden md:mt-14">
        <div className="grid grid-cols-[1.4fr_1fr_1fr] items-center border-b px-5 py-3 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ borderColor: "var(--lm-hair)", color: "var(--lm-ink-4)" }}>
          <span>Feature</span>
          <span>Linkit</span>
          <span>Linktree</span>
        </div>
        {COMPARE_ROWS.map((r, i) => (
          <div key={r.row} className={`grid grid-cols-[1.4fr_1fr_1fr] items-center px-5 py-4 text-[14px] ${i % 2 ? "" : ""}`} style={{ borderTop: i === 0 ? "0" : "1px solid var(--lm-hair)" }}>
            <span style={{ color: "var(--lm-ink-2)" }}>{r.row}</span>
            <span className="font-semibold" style={{ color: "var(--lm-ink)" }}>{r.linkit}</span>
            <span style={{ color: "var(--lm-ink-3)" }}>{r.other}</span>
          </div>
        ))}
      </Card>
    </SectionShell>
  );
}

// ---------------------------------------------------------------- PRICING
export function PricingBlock() {
  const [annual, setAnnual] = useState(true);
  const price = annual ? "₹417" : "₹499";
  const suffix = annual ? "per month, billed ₹4,999 annually" : "per month";
  const includes = [
    "Unlimited links",
    "Full page customization",
    "AI-assisted page creation",
    "Analytics (views, clicks, CTR)",
    "Individual link analytics",
    "Lead capture inbox + email alerts",
    "Custom sections",
    "Draft and publishing controls",
    "QR code + Share block",
    "Data export (CSV)",
    "Priority support",
  ];
  return (
    <SectionShell id="pricing" dataTestId="pricing-section" className="bg-[color:var(--lm-cream-2)]/60">
      <div className="mx-auto max-w-3xl text-center">
        <Eyebrow>Pricing</Eyebrow>
        <EditorialHeading size="xl">One plan. Everything included.</EditorialHeading>
        <Lede className="mt-4">30 days free — no payment method required to start.</Lede>
      </div>
      <div className="mt-8 flex items-center justify-center gap-2">
        <div className="inline-flex rounded-full border p-1 text-[13px] font-semibold" style={{ borderColor: "var(--lm-hair)", background: "var(--lm-paper)" }}>
          <button onClick={() => setAnnual(false)} data-testid="pricing-toggle-monthly" className={`rounded-full px-4 py-1.5 ${!annual ? "bg-[color:var(--lm-ink)] text-white" : "text-[color:var(--lm-ink-3)]"}`}>Monthly</button>
          <button onClick={() => setAnnual(true)} data-testid="pricing-toggle-annual" className={`rounded-full px-4 py-1.5 ${annual ? "bg-[color:var(--lm-ink)] text-white" : "text-[color:var(--lm-ink-3)]"}`}>Annual <span className="ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold" style={{ background: "var(--lm-terra-light)", color: "var(--lm-terra-dark)" }}>Save ₹989</span></button>
        </div>
      </div>
      <div className="mx-auto mt-10 grid max-w-lg">
        <Card className="p-8 sm:p-10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--lm-terra)" }}>Linkit Pro</p>
              <p className="mt-1 text-[13px]" style={{ color: "var(--lm-ink-3)" }}>Everything to run a serious link-in-bio.</p>
            </div>
            {annual && <Pill tone="terra">Best value</Pill>}
          </div>
          <div className="mt-8 flex items-baseline gap-2">
            <span className="text-[52px] font-bold leading-none" style={{ fontFamily: "var(--lm-display)", color: "var(--lm-ink)" }}>{price}</span>
            <span className="text-[13px]" style={{ color: "var(--lm-ink-3)" }}>/month</span>
          </div>
          <p className="mt-1 text-[13px]" style={{ color: "var(--lm-ink-3)" }}>{suffix}</p>
          <Button to="/signup" size="lg" variant="primary" className="mt-6 w-full" testid="pricing-cta">
            Start 30 days free <ArrowRight size={16} />
          </Button>
          <p className="mt-3 text-center text-[12px]" style={{ color: "var(--lm-ink-4)" }}>No payment method required.</p>
          <div className="mt-8 grid grid-cols-1 gap-2.5 border-t pt-6" style={{ borderColor: "var(--lm-hair)" }}>
            {includes.map((f) => (
              <div key={f} className="flex items-start gap-2.5 text-[14px]" style={{ color: "var(--lm-ink-2)" }}>
                <Check size={16} className="mt-1 shrink-0" style={{ color: "var(--lm-terra)" }} /> {f}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------- FAQ
const FAQ_ITEMS = [
  { q: "Is there a free trial?", a: "Yes. You get 30 days free with no payment method required. At day 23, if you'd like an extra 15 days, add a payment method to extend to 45 days. Your first charge only happens after the full trial." },
  { q: "Can I cancel any time?", a: "Yes. Cancel before your first billing date and you won't be charged. After you're a paying subscriber, cancel any time and access continues until the end of the current period." },
  { q: "Do you offer refunds?", a: "See our refund policy — we're fair and transparent about it." },
  { q: "Will my page always be free to visit?", a: "Yes. Public visitors never pay or sign up. Only the creator building the page has a subscription." },
  { q: "How does AI fit in?", a: "AI can build a first draft based on what you describe, but you can also start blank. Every block is yours to edit, reorder, hide, or delete." },
  { q: "Do you offer support?", a: "Yes — email hello@linkit.app and we get back within 1 business day." },
];
function FAQ() {
  const [open, setOpen] = useState(-1);
  return (
    <SectionShell dataTestId="faq-section">
      <div className="max-w-3xl">
        <Eyebrow>FAQ</Eyebrow>
        <EditorialHeading size="xl">Questions people ask us.</EditorialHeading>
      </div>
      <div className="mt-10 max-w-3xl divide-y" style={{ borderColor: "var(--lm-hair)" }}>
        {FAQ_ITEMS.map((f, i) => (
          <div key={i} className="border-t py-4" style={{ borderColor: "var(--lm-hair)" }}>
            <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-${i}`} className="flex w-full items-center justify-between gap-4 text-left">
              <span className="text-[16px] font-semibold" style={{ color: "var(--lm-ink)" }}>{f.q}</span>
              <ChevronDown size={16} className={`shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} style={{ color: "var(--lm-ink-3)" }} />
            </button>
            {open === i && <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "var(--lm-ink-3)" }}>{f.a}</p>}
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------- FINAL CTA
function FinalCTA() {
  return (
    <SectionShell dataTestId="final-cta">
      <div className="mx-auto max-w-3xl text-center">
        <EditorialHeading size="xl">Your bio, working harder for you.</EditorialHeading>
        <Lede className="mx-auto mt-5 max-w-2xl">
          Give your audience a page that reflects what you do — and get data on what they actually care about.
        </Lede>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button to="/signup" size="lg" variant="primary">Build my page free <ArrowRight size={16} /></Button>
          <Button to="/create/pricing" size="lg" variant="secondary">See pricing</Button>
        </div>
        <p className="mt-4 text-[13px]" style={{ color: "var(--lm-ink-3)" }}>30 days free · no card required</p>
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------- LANDING PAGE
export default function MarketingHome() {
  const [branding, setBranding] = useState(null);
  useEffect(() => {
    api.get("/linkit/branding").then((r) => setBranding(r.data)).catch(() => setBranding({}));
  }, []);
  return (
    <MarketingLayout>
      <Hero />
      <FounderSection branding={branding || {}} />
      <HowItWorks />
      <MobileAppSection />
      <AnalyticsSection />
      <LeadsSection />
      <CustomizationSection />
      <ExamplesSection />
      <CompareSection />
      <PricingBlock />
      <FAQ />
      <FinalCTA />
    </MarketingLayout>
  );
}
